export type SoundStyle =
  | "tap"
  | "toggle-on"
  | "toggle-off"
  | "success"
  | "error";

/**
 * Web sound implementation using the Web Audio API.
 * Generates simple synthesized tones instead of loading audio files.
 */

let enabled = true;
let audioCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AudioCtx =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (AudioCtx) audioCtx = new AudioCtx();
  }
  return audioCtx;
}

export function setSoundsEnabled(on: boolean): void {
  enabled = on;
}

export function getSoundsEnabled(): boolean {
  return enabled;
}

/** No-op on web — sounds are synthesized, no assets to preload. */
export async function preloadSounds(): Promise<void> {}

/** Play a synthesized UI tone. */
export function playSound(style: SoundStyle): void {
  if (!enabled) return;
  const ctx = getCtx();
  if (!ctx) return;

  // Resume context if suspended (autoplay policy)
  if (ctx.state === "suspended") {
    ctx.resume();
  }

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);

  const now = ctx.currentTime;
  gain.gain.setValueAtTime(0.08, now);
  osc.start(now);

  switch (style) {
    case "tap":
      osc.frequency.setValueAtTime(800, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
      osc.stop(now + 0.06);
      break;
    case "toggle-on":
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.linearRampToValueAtTime(900, now + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
      osc.stop(now + 0.1);
      break;
    case "toggle-off":
      osc.frequency.setValueAtTime(700, now);
      osc.frequency.linearRampToValueAtTime(400, now + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
      osc.stop(now + 0.1);
      break;
    case "success":
      osc.frequency.setValueAtTime(523, now);
      osc.frequency.setValueAtTime(659, now + 0.1);
      osc.frequency.setValueAtTime(784, now + 0.2);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc.stop(now + 0.35);
      break;
    case "error":
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(200, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.stop(now + 0.15);
      break;
  }
}

/** No-op on web. */
export async function unloadSounds(): Promise<void> {
  audioCtx = null;
}
